import {
	useApiGetComputeCostMetrics,
	useApiGetComputeCostTimeseries,
} from '@repo/api-codegen';
import { keepPreviousData } from '@tanstack/react-query';
import { noop } from 'lodash-es';
import { memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthUser } from '../../api';
import { ComputeCostMetricCards } from './components/cards/ComputeCostMetricCards';
import { ComputeCostChart } from './components/charts/ComputeCostChart';
import { MetricsOverviewTab } from './components/MetricsOverviewTab';
import { MetricsOverviewTabSkeleton } from './components/MetricsOverviewTabSkeleton';
import { useOverviewMetricDatetime } from './hooks/useMetricOverviewDatetime';
import type {
	GenericOverviewTabControllerProps,
	ViewOptionType,
} from './types';
import { isValidTimeRange, isValidViewOption } from './types.ts';

type ComputeCostGroupByType = 'job_type' | 'execution_type' | 'workspace';
type ComputeCostDisplayAsType = 'cost' | 'duration';

const computeCostGroupByFilterOptions = [
	{ label: 'Group by: Job Type', value: 'job_type' },
	{ label: 'Group by: Execution Type', value: 'execution_type' },
	{ label: 'Group by: Workspace', value: 'workspace' },
];

const computeCostDisplayAsFilterOptions = [
	{ label: 'Display as: Cost', value: 'cost' },
	{ label: 'Display as: Duration', value: 'duration' },
];

export const ComputeCostOverviewTabController = memo(
	({ onMetricCardClick }: GenericOverviewTabControllerProps) => {
		const { user } = useAuthUser();
		const {
			timeRangeValue,
			setTimeRangeValue,
			dateRangeOption,
			setDateRangeOption,
			dateFilter,
			setDateFilter,
		} = useOverviewMetricDatetime({ cacheKey: 'compute-cost' });
		const navigate = useNavigate();
		const [groupByFilter, setGroupByFilter] =
			useState<ComputeCostGroupByType>('job_type');
		const [displayAsFilter, setDisplayAsFilter] =
			useState<ComputeCostDisplayAsType>('cost');

		// Create wrapper functions to handle type conversion with validation
		const handleSetDateFilter = useCallback(
			(value: string) => {
				if (isValidTimeRange(value)) {
					setDateFilter(value);
				}
			},
			[setDateFilter]
		);

		const handleSetDateRangeOption = useCallback(
			(value: string) => {
				if (isValidViewOption(value)) {
					setDateRangeOption(value);
				}
			},
			[setDateRangeOption]
		);

		const { data: metrics, isLoading: isMetricsLoading } =
			useApiGetComputeCostMetrics(
				{
					queryParams: {
						time_range: dateFilter as
							| '24h'
							| '7d'
							| '30d'
							| '90d'
							| '180d'
							| '365d',
					},
				},
				{ placeholderData: keepPreviousData }
			);

		const { data: timeseries, isPending: isTimeseriesLoading } =
			useApiGetComputeCostTimeseries(
				{
					queryParams: {
						time_range: dateFilter as
							| '24h'
							| '7d'
							| '30d'
							| '90d'
							| '180d'
							| '365d',
						group_by: groupByFilter,
						view_option: dateRangeOption as ViewOptionType,
					},
				},
				{ placeholderData: keepPreviousData }
			);

		const handleGroupByFilterChange = useCallback(
			(filterTypes: string[]) => {
				const newGroupBy = filterTypes[0] as ComputeCostGroupByType;
				if (newGroupBy && newGroupBy !== groupByFilter) {
					setGroupByFilter(newGroupBy);
				}
			},
			[groupByFilter]
		);

		const handleDisplayAsFilterChange = useCallback(
			(filterTypes: string[]) => {
				const newDisplayAs = filterTypes[0] as ComputeCostDisplayAsType;
				if (newDisplayAs && newDisplayAs !== displayAsFilter) {
					setDisplayAsFilter(newDisplayAs);
				}
			},
			[displayAsFilter]
		);

		// Show loading state
		if (isMetricsLoading || isTimeseriesLoading) {
			return <MetricsOverviewTabSkeleton />;
		}

		return (
			<MetricsOverviewTab
				filterOptions={[
					computeCostGroupByFilterOptions,
					computeCostDisplayAsFilterOptions,
				]}
				filterTypes={[groupByFilter, displayAsFilter]}
				setFilterTypes={(filterTypes: string[]) => {
					// Handle both filters based on their position
					if (filterTypes[0] !== groupByFilter) {
						handleGroupByFilterChange([filterTypes[0]]);
					}
					if (filterTypes[1] !== displayAsFilter) {
						handleDisplayAsFilterChange([filterTypes[1]]);
					}
				}}
			>
				<ComputeCostMetricCards
					computeCostMetrics={metrics}
					onMetricCardClick={noop}
				/>
				<ComputeCostChart
					timeseries={timeseries}
					dateFilter={dateFilter || '24h'}
					setDateFilter={handleSetDateFilter}
					setTimeRangeValue={setTimeRangeValue}
					timeRangeValue={timeRangeValue}
					dateRangeOption={dateRangeOption || 'day'}
					setDateRangeOption={handleSetDateRangeOption}
					displayAs={displayAsFilter}
				/>
			</MetricsOverviewTab>
		);
	}
);

ComputeCostOverviewTabController.displayName =
	'ComputeCostOverviewTabController';
